import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import securityApi from '../api/securityApi';

// Context for the autonomous pentest ("Applications") feature. Modeled closely
// on ScanContext: a provider exposing a value object, a hook that throws if used
// outside the provider, and a setInterval polling loop adapted from
// ScanContext.startScan.
const PentestContext = createContext();

// Normalize a single pentest finding into the shape VulnerabilityItem /
// FindingDetailPage expect. DAST findings are located by url/method/parameter +
// evidence rather than file:line, so we preserve those fields and leave
// file/line absent.
// Statuses after which a pentest will not change. The backend writes 'completed'; 'complete'
// is kept for safety.
const TERMINAL_STATUSES = new Set(['complete', 'completed', 'failed', 'error', 'cancelled']);

const normalizeFinding = (finding, index) => {
  const remediation = Array.isArray(finding.remediation)
    ? finding.remediation
    : typeof finding.remediation === 'object' && finding.remediation !== null
      ? finding.remediation
      : finding.remediation
        ? [finding.remediation]
        : [];

  // VulnerabilityItem keys and expands items by id, sharing ScanContext's
  // expand state with SAST findings. Namespace the id so it is always present,
  // unique within the list, and can't collide with a code-scan finding id.
  const sourceId = finding.id ?? finding.finding_id ?? finding.findingId;
  const id = `pentest-${sourceId ?? `idx-${index}`}`;

  return {
    ...finding,
    id,
    sourceId,
    title: finding.title || finding.message || '',
    description: finding.description || finding.message || '',
    severity: (finding.severity || '').toLowerCase() || null,
    category: finding.category || 'dynamic_analysis',
    scanner: finding.scanner || 'dast',
    remediation,
  };
};

export const PentestProvider = ({ children }) => {
  // Applications + targets
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [creatingApplication, setCreatingApplication] = useState(false);

  // Ownership verification state (per target id)
  const [verifyInfoMap, setVerifyInfoMap] = useState({}); // targetId -> { method, token, instructions }
  const [verifiedMap, setVerifiedMap] = useState({}); // targetId -> bool
  const [verifyingMap, setVerifyingMap] = useState({}); // targetId -> bool

  // Running pentest state. Field names mirror ScanContext so the reused
  // ScanAnimation can read equivalent data via props.
  const [pentestId, setPentestId] = useState(null);
  const [pentestStatus, setPentestStatus] = useState(null);
  const [pentestPhase, setPentestPhase] = useState(null);
  const [pentestProgress, setPentestProgress] = useState(0);
  const [pentestLogs, setPentestLogs] = useState([]);
  const [pentestStage, setPentestStage] = useState('initializing');
  const [running, setRunning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Whether the backend could actually PARSE the engine's output. An empty findings list
  // means two opposite things -- 'proved nothing exploitable' vs 'we could not read the
  // output' -- and only the second must never be shown as a clean result.
  const [findingsParsed, setFindingsParsed] = useState(null);
  // True when the findings request failed, so an empty list is "unknown", not "clean".
  const [findingsFetchFailed, setFindingsFetchFailed] = useState(false);

  // Results
  const [findings, setFindings] = useState([]);
  const [reportUrl, setReportUrl] = useState(null);
  const [loadingFindings, setLoadingFindings] = useState(false);

  const [error, setError] = useState(null);

  // ---- Applications -------------------------------------------------------

  const loadApplications = useCallback(async () => {
    setLoadingApplications(true);
    try {
      const data = await securityApi.getApplications();
      // Handle both an array and { applications: [...] }.
      const list = Array.isArray(data) ? data : (data?.applications || []);
      setApplications(list);
      setError(null);
    } catch (err) {
      setError(`Failed to load applications: ${err.message}`);
      toast.error('Failed to load applications');
      setApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  }, []);

  const createApplication = useCallback(async ({ name, repoUrl }) => {
    setCreatingApplication(true);
    try {
      const created = await securityApi.createApplication({ name, repoUrl });
      await loadApplications();
      toast.success('Application created');
      return created;
    } catch (err) {
      setError(`Failed to create application: ${err.message}`);
      toast.error('Failed to create application');
      return null;
    } finally {
      setCreatingApplication(false);
    }
  }, [loadApplications]);

  const updateApplication = useCallback(async (appId, { name, repoUrl }) => {
    setCreatingApplication(true);
    try {
      const updated = await securityApi.updateApplication(appId, { name, repoUrl });
      await loadApplications();
      toast.success('Application updated');
      return updated;
    } catch (err) {
      setError(`Failed to update application: ${err.message}`);
      toast.error('Failed to update application');
      return null;
    } finally {
      setCreatingApplication(false);
    }
  }, [loadApplications]);

  const getApplication = useCallback((appId) => {
    return applications.find(
      (app) => String(app.id) === String(appId)
    );
  }, [applications]);

  const createTarget = useCallback(async (appId, { targetUrl, environment }) => {
    try {
      const created = await securityApi.createTarget(appId, { targetUrl, environment });
      await loadApplications();
      toast.success('Target added');
      return created;
    } catch (err) {
      setError(`Failed to add target: ${err.message}`);
      toast.error('Failed to add target');
      return null;
    }
  }, [loadApplications]);

  // ---- Ownership verification --------------------------------------------

  const startVerification = useCallback(async (targetId) => {
    setVerifyingMap((prev) => ({ ...prev, [targetId]: true }));
    try {
      const info = await securityApi.startTargetVerification(targetId);
      setVerifyInfoMap((prev) => ({ ...prev, [targetId]: info }));
      return info;
    } catch (err) {
      toast.error('Failed to start verification');
      return null;
    } finally {
      setVerifyingMap((prev) => ({ ...prev, [targetId]: false }));
    }
  }, []);

  const checkVerification = useCallback(async (targetId) => {
    setVerifyingMap((prev) => ({ ...prev, [targetId]: true }));
    try {
      const result = await securityApi.checkTargetVerification(targetId);
      const verified = !!result?.verified;
      setVerifiedMap((prev) => ({ ...prev, [targetId]: verified }));
      if (verified) {
        toast.success('Ownership verified');
      } else {
        toast('Not verified yet — complete the steps and check again', {
          icon: '⏳',
        });
      }
      return verified;
    } catch (err) {
      toast.error('Failed to check verification');
      return false;
    } finally {
      setVerifyingMap((prev) => ({ ...prev, [targetId]: false }));
    }
  }, []);

  // ---- Findings / report --------------------------------------------------

  // The id each fetch was last issued for. A response whose id no longer
  // matches was superseded (e.g. the user switched pentests) and is dropped.
  const findingsRequestRef = useRef(null);
  const reportRequestRef = useRef(null);

  const fetchFindings = useCallback(async (id) => {
    const activeId = id || pentestId;
    if (!activeId) return;
    const requestId = String(activeId);
    findingsRequestRef.current = requestId;
    setFindings([]);
    setFindingsParsed(null);
    setFindingsFetchFailed(false);
    setLoadingFindings(true);
    try {
      const data = await securityApi.getPentestFindings(activeId);
      if (findingsRequestRef.current !== requestId) return;
      const list = Array.isArray(data) ? data : (data?.findings || []);
      setFindings(list.map(normalizeFinding));
      // undefined on an older backend that does not report provenance; treat that as unknown
      // rather than asserting either way.
      setFindingsParsed(
        typeof data?.findingsParsed === 'boolean' ? data.findingsParsed : null
      );
    } catch (err) {
      if (findingsRequestRef.current !== requestId) return;
      setFindingsFetchFailed(true);
      setError(`Failed to fetch findings: ${err.message}`);
      toast.error('Failed to fetch findings');
    } finally {
      if (findingsRequestRef.current === requestId) setLoadingFindings(false);
    }
  }, [pentestId]);

  const fetchReport = useCallback(async (id) => {
    const activeId = id || pentestId;
    if (!activeId) return null;
    const requestId = String(activeId);
    reportRequestRef.current = requestId;
    setReportUrl(null);
    try {
      const data = await securityApi.getPentestReport(activeId);
      if (reportRequestRef.current !== requestId) return null;
      // Build the href from our own API base rather than using the server's relative
      // reportUrl: the UI and API are on different origins, so a relative path would
      // resolve against the UI's origin and 404.
      if (data?.available || data?.reportUrl) {
        setReportUrl(securityApi.getPentestReportDownloadUrl(activeId));
      }
      return data;
    } catch (err) {
      // A missing report is not fatal — the findings list still renders.
      console.error('Failed to fetch report:', err);
      return null;
    }
  }, [pentestId]);

  // ---- Poll a pentest (polling copied from ScanContext.startScan) ---------

  // Only one pentest is polled at a time. Starting, opening or resetting a
  // pentest clears the previous loop so stale loops can't overwrite state.
  const pollIntervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // `initialLogId` is the log cursor the caller already holds logs up to, so the first poll
  // doesn't re-send (and re-append) lines that are already on screen.
  const pollPentest = useCallback((id, initialLogId = 0) => {
    stopPolling();

    let consecutiveErrors = 0;
    let lastLogId = initialLogId;
    // setInterval doesn't wait for an async tick; a slow status call (30s timeout) would
    // otherwise overlap the next one and both would append the same log lines.
    let inFlight = false;
    const MAX_CONSECUTIVE_ERRORS = 5;
    // A pentest runs ~1-1.5h (Shannon's own estimate) and the backend caps it at 150 min
    // (DAST_RUN_TIMEOUT_SECONDS), so poll on a wall-clock deadline past that rather than an
    // attempt count. The previous 900 attempts x 2s gave up after 30 minutes and surfaced an
    // error while the run was still perfectly healthy.
    const POLL_INTERVAL_MS = 5000;
    const DEADLINE_MS = Date.now() + 170 * 60 * 1000;

    const interval = setInterval(async () => {
      if (inFlight) return;
      if (Date.now() > DEADLINE_MS) {
        stopPolling();
        setError('Stopped watching this pentest after 170 minutes. It may still be running in the background; reopen the results page to check.');
        setRunning(false);
        return;
      }

      inFlight = true;
      try {
        const statusData = await securityApi.getPentestStatus(id, lastLogId);
        // This loop was replaced or stopped while the request was in flight.
        if (pollIntervalRef.current !== interval) return;
        consecutiveErrors = 0;

        if (statusData.status === 'queued') {
          setPentestStage('queued');
          return;
        }
        if (statusData.status) setPentestStatus(statusData.status);
        if (statusData.currentPhase !== undefined) setPentestPhase(statusData.currentPhase);
        if (statusData.progress !== undefined) setPentestProgress(statusData.progress);
        setPentestStage(statusData.currentPhase || 'scanning');

        // Logs are incremental now (we send lastLogId, the endpoint returns only newer lines
        // plus a logCount cursor), so APPEND rather than replace.
        if (statusData.logs && statusData.logs.length > 0) {
          setPentestLogs((prev) => [...prev, ...statusData.logs]);
        }
        if (typeof statusData.logCount === 'number') {
          lastLogId = statusData.logCount;
        }

        if (statusData.status === 'failed' || statusData.status === 'error') {
          stopPolling();
          setError('The pentest failed. Please review the logs and try again.');
          setRunning(false);
          return;
        }

        if (statusData.status === 'cancelled') {
          stopPolling();
          setError('The pentest was cancelled.');
          setRunning(false);
          return;
        }

        // 'completed' is what the backend writes; 'complete' kept for safety.
        if (
          statusData.progress >= 100
          || statusData.status === 'completed'
          || statusData.status === 'complete'
        ) {
          stopPolling();
          setRunning(false);
          fetchFindings(id);
          fetchReport(id);
        }
      } catch (err) {
        if (pollIntervalRef.current !== interval) return;
        consecutiveErrors++;
        console.error(`Error polling pentest status (attempt ${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, err);
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          stopPolling();
          setError('Network error while polling pentest status. The pentest may still be running.');
          setRunning(false);
        }
      } finally {
        inFlight = false;
      }
    }, POLL_INTERVAL_MS);

    pollIntervalRef.current = interval;
  }, [stopPolling, fetchFindings, fetchReport]);

  const clearPentestState = useCallback(() => {
    stopPolling();
    findingsRequestRef.current = null;
    reportRequestRef.current = null;
    setPentestId(null);
    setPentestStatus(null);
    setPentestPhase(null);
    setPentestProgress(0);
    setPentestLogs([]);
    setPentestStage('initializing');
    setRunning(false);
    setFindings([]);
    setFindingsParsed(null);
    setFindingsFetchFailed(false);
    setReportUrl(null);
    setLoadingFindings(false);
    setError(null);
  }, [stopPolling]);

  const cancelPentest = useCallback(async (id) => {
    const target = id || pentestId;
    if (!target) return null;
    setCancelling(true);
    try {
      const result = await securityApi.cancelPentest(target);
      // Stop polling ourselves rather than waiting for the status to flip: a running engine
      // takes up to ~15s to actually die, and continuing to poll in the meantime would keep
      // showing it as running right after the user asked to stop it.
      // Cancelling some other run (e.g. a queued row in the history table) must not stop
      // the poller for the pentest this context is actually watching.
      if (pentestId != null && String(target) === String(pentestId)) {
        stopPolling();
        setRunning(false);
        setPentestStatus('cancelled');
      }
      if (result?.alreadyFinished) {
        toast.success('That pentest had already finished');
      } else if (result?.engineStopping) {
        toast.success('Cancelling — stopping the scan engine, this takes a few seconds');
      } else {
        toast.success('Pentest cancelled');
      }
      return result;
    } catch (err) {
      setError(`Failed to cancel pentest: ${err.message}`);
      toast.error('Failed to cancel pentest');
      return null;
    } finally {
      setCancelling(false);
    }
  }, [pentestId, stopPolling]);

  const startPentest = useCallback(async ({
    applicationId,
    targetId,
    scope,
    rateLimit,
    auth,
    attestation,
  }) => {
    try {
      clearPentestState();
      setRunning(true);

      const response = await securityApi.startPentest({
        applicationId,
        targetId,
        scope,
        rateLimit,
        auth,
        attestation,
      });
      // Without an id there is nothing to poll; bail out rather than polling
      // /pentest/undefined/status for 30 minutes.
      if (response?.pentestId == null || response.pentestId === '') {
        setError('Failed to start pentest: server did not return a pentest ID');
        toast.error('Failed to start pentest');
        setRunning(false);
        return null;
      }
      // Route params are strings; store the id as one so comparisons hold
      // whether the API returns a number or a string.
      const newPentestId = String(response.pentestId);
      setPentestId(newPentestId);
      pollPentest(newPentestId);
      return newPentestId;
    } catch (err) {
      setError('Failed to start pentest: ' + err.message);
      toast.error('Failed to start pentest');
      setRunning(false);
      return null;
    }
  }, [clearPentestState, pollPentest]);

  // Load an existing pentest by id (deep link, refresh, history row). Resumes
  // polling if it is still in progress, otherwise loads its results.
  const openPentest = useCallback(async (id) => {
    const activeId = String(id);
    clearPentestState();
    setPentestId(activeId);
    setLoadingFindings(true);
    findingsRequestRef.current = activeId;

    let statusData = null;
    try {
      statusData = await securityApi.getPentestStatus(activeId);
    } catch (err) {
      // Fall through and try to load whatever findings exist.
      console.error('Failed to fetch pentest status:', err);
    }
    // Another pentest was opened or started while the status was loading.
    if (findingsRequestRef.current !== activeId) return;

    const status = statusData?.status;
    const inProgress = status === 'queued'
      || (status && !TERMINAL_STATUSES.has(status) && !(statusData.progress >= 100));

    if (inProgress) {
      setLoadingFindings(false);
      setRunning(true);
      setPentestStatus(status);
      setPentestStage(status === 'queued' ? 'queued' : (statusData.currentPhase || 'scanning'));
      if (statusData.currentPhase !== undefined) setPentestPhase(statusData.currentPhase);
      if (statusData.progress !== undefined) setPentestProgress(statusData.progress);
      // Seed the log pane only when the cursor is known, and resume polling from it;
      // otherwise start empty and let the first poll deliver everything once.
      const hasCursor = typeof statusData.logCount === 'number';
      if (hasCursor && statusData.logs?.length) setPentestLogs(statusData.logs);
      pollPentest(activeId, hasCursor ? statusData.logCount : 0);
      return;
    }

    if (status) setPentestStatus(status);
    if (status === 'failed' || status === 'error') {
      setError('The pentest failed. Please review the logs and try again.');
    }
    fetchFindings(activeId);
    fetchReport(activeId);
  }, [clearPentestState, pollPentest, fetchFindings, fetchReport]);

  const resetPentest = clearPentestState;

  const value = {
    // applications
    applications,
    loadingApplications,
    creatingApplication,
    loadApplications,
    createApplication,
    updateApplication,
    getApplication,
    createTarget,
    // verification
    verifyInfoMap,
    verifiedMap,
    verifyingMap,
    startVerification,
    checkVerification,
    // running pentest
    cancelPentest,
    cancelling,
    findingsParsed,
    findingsFetchFailed,
    pentestId,
    pentestStatus,
    pentestPhase,
    pentestProgress,
    pentestLogs,
    pentestStage,
    running,
    startPentest,
    openPentest,
    resetPentest,
    // results
    findings,
    reportUrl,
    loadingFindings,
    fetchFindings,
    fetchReport,
    // misc
    error,
    setError,
  };

  return (
    <PentestContext.Provider value={value}>
      {children}
    </PentestContext.Provider>
  );
};

export const usePentest = () => {
  const context = useContext(PentestContext);
  if (context === undefined) {
    throw new Error('usePentest must be used within a PentestProvider');
  }
  return context;
};

export default PentestContext;
